import * as React from 'react'
import type { ToggleProps } from '@components/molecules';
import { Toggle } from '@components/molecules';
import { CollapsibleCard } from '@components/organisms';

export interface ToggleboxProps {
	title: string;
	toggles: ToggleProps[];
	isCollapsed: boolean;
	toggleCollapse: () => void;
	withCounter?: boolean;
	withSelectAll?: boolean;
	selectAllLabel?: string;
	onSelectAllChange?: (checked: boolean) => void;
	'data-aid'?: string;
}

const ToggleboxCounter = ({ toggles, activeTogglesLength }: { toggles: ToggleProps[], activeTogglesLength: number }) => {
	if (!activeTogglesLength) return null;
	return (
		<span className="text-sm font-semibold text-success">
			{activeTogglesLength} / {toggles.length} selected
		</span>
	);
};

export const Togglebox = ({
	title,
	toggles,
	isCollapsed,
	toggleCollapse,
	withCounter = true,
	withSelectAll,
	selectAllLabel = '',
	"data-aid": dataAid,
	onSelectAllChange,
	...props
}: ToggleboxProps) => {
	const { numOfActiveToggles, allTogglesActive } = React.useMemo(() => {
		const numOfActiveToggles = toggles.filter(toggle => toggle.checked).length;
		return {
			numOfActiveToggles,
			allTogglesActive: numOfActiveToggles === toggles.length,
		}
	}, [toggles]);

	const onToggleChange = (checked: boolean, callback: ((checked: boolean) => void) | undefined) => {
		callback?.(checked);
	}

	const handleSelectAllChange = (checked: boolean) => {
		if (onSelectAllChange) {
			onSelectAllChange(checked);
		} else {
			for (const toggle of toggles) {
				toggle.onCheckedChange?.(checked);
			}
		}
	}

	return (
		<CollapsibleCard
			{...props}
			data-aid={dataAid}
			toggleCollapse={toggleCollapse}
			isCollapsed={isCollapsed}
			title={title}
			headerSubComponent={withCounter && <ToggleboxCounter toggles={toggles} activeTogglesLength={numOfActiveToggles} />}
		>
			<div className="flex w-full flex-col gap-y-4">
				{withSelectAll && (
					<div className="mb-2">
						<Toggle
							label={selectAllLabel}
							labelProps={{ weight: 'bold' }}
							checked={allTogglesActive}
							onCheckedChange={handleSelectAllChange}
						/>
					</div>
				)}
				{toggles.map(({ onCheckedChange, ...toggle}, index) => (
					<Toggle key={index} {...toggle} onCheckedChange={(checked) => onToggleChange(checked, onCheckedChange)} />
				))}
			</div>
		</CollapsibleCard>
	);
};
